import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  ClientRow, CompanyDetail, CompanyRow, Duplicate, FieldDef, FilterNode, HistoryItem, ImportListItem, ImportReport, Issue,
  Me, RoleDef, UserRow,
} from "./types";

export const useMe = () =>
  useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/auth/me"), retry: false, staleTime: 60_000 });

export const useFields = (includeArchived = false) =>
  useQuery({
    queryKey: ["fields", includeArchived],
    queryFn: () => api<FieldDef[]>(`/fields${includeArchived ? "?include_archived=true" : ""}`),
    staleTime: 30_000,
  });

export interface SearchParams {
  q: string;
  filters: FilterNode | null;
}

export const useCompanySearch = (p: SearchParams) =>
  useInfiniteQuery({
    queryKey: ["companies", p],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ items: CompanyRow[]; next_after_id: number | null }>("/companies/search", {
        json: { q: p.q || null, filters: p.filters, after_id: pageParam || null, limit: 100 },
      }),
    getNextPageParam: (last) => last.next_after_id ?? undefined,
  });

export const useCompanyCount = (p: SearchParams) =>
  useQuery({
    queryKey: ["companies-count", p],
    queryFn: () => api<{ companies: number; contacts: number }>("/companies/count", { json: { q: p.q || null, filters: p.filters } }),
  });

export const useCompany = (id: number | null) =>
  useQuery({ queryKey: ["company", id], queryFn: () => api<CompanyDetail>(`/companies/${id}`), enabled: id != null });

export const useCompanyHistory = (id: number | null, enabled: boolean) =>
  useQuery({
    queryKey: ["company-history", id],
    queryFn: () => api<HistoryItem[]>(`/companies/${id}/history`),
    enabled: id != null && enabled,
  });

export function usePatchCompany(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { version: number; changes: Record<string, unknown> }) =>
      api<CompanyRow>(`/companies/${id}`, { method: "PATCH", json: b }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["company", id] });
      qc.invalidateQueries({ queryKey: ["company-history", id] });
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

export const useImports = () =>
  useQuery({
    queryKey: ["imports"],
    queryFn: () => api<ImportListItem[]>("/imports"),
    refetchInterval: (q) => (q.state.data?.some((i) => !["completed", "failed", "awaiting_mapping", "cancelled"].includes(i.status)) ? 2000 : false),
  });

export const useImport = (id: number) =>
  useQuery({
    queryKey: ["import", id],
    queryFn: () => api<ImportReport>(`/imports/${id}`),
    refetchInterval: (q) => (["analyzing", "queued", "running", "uploaded"].includes(q.state.data?.status ?? "analyzing") ? 1500 : false),
  });

export const useImportIssues = (id: number, severity: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["import-issues", id, severity],
    queryFn: () => api<{ items: Issue[]; next_after_id: number | null }>(`/imports/${id}/issues?limit=500${severity ? `&severity=${severity}` : ""}`),
    enabled,
  });

export const useDuplicates = (status = "pending") =>
  useQuery({ queryKey: ["duplicates", status], queryFn: () => api<Duplicate[]>(`/duplicates?status=${status}`) });

export const useUsers = () => useQuery({ queryKey: ["users"], queryFn: () => api<UserRow[]>("/users") });

export const useRoles = () => useQuery({ queryKey: ["roles"], queryFn: () => api<RoleDef[]>("/roles"), staleTime: 300_000 });

export const useClients = (enabled = true) =>
  useQuery({ queryKey: ["clients"], queryFn: () => api<ClientRow[]>("/clients"), enabled, staleTime: 60_000 });
